import { Box, Button, Link as MuiLink, Typography } from "@mui/material";
import { TasksDisplay } from "components/Tasks/TasksDisplay";
import {
  CALENDAR_DEFAULT_TASK_STATUSES,
  LIST_DEFAULT_TASK_STATUSES,
} from "components/Tasks/taskStatusFilter";
import { Permission } from "generated-graphql/graphql";
import { useAuthorization } from "hooks/useAuthorization";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFacilityReadOnly } from "hooks/useFacilityReadOnly";
import { useCallback, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AddTaskDialog } from "routes/Customer/Tasks/AddTaskDialog";
import { TaskFilterChips } from "routes/Customer/Tasks/TaskFilterChips";

export const TasksSection: React.FC = () => {
  const { tenantId, facilityId } = useParams<{ tenantId: string; facilityId: string }>();
  const { hasPermissions } = useAuthorization();
  const { user } = useCurrentUser();
  const { isReadOnly } = useFacilityReadOnly(facilityId);
  const [openAddModal, setOpenAddModal] = useState(false);

  const canAddTask = useMemo(() => {
    if (isReadOnly) return false;
    return hasPermissions([Permission.WriteAllFacility]) || (user?.facilities.length ?? 0) > 0;
  }, [hasPermissions, user?.facilities.length, isReadOnly]);

  const commandButtons = useCallback(() => {
    if (!canAddTask) return [] as React.ReactNode[];
    return [
      <Button key="add-task-button" variant="contained" onClick={() => setOpenAddModal(true)}>
        Add Task
      </Button>,
    ];
  }, [canAddTask]);

  const base = `tenantId:${tenantId} facilityId:${facilityId}`;
  const defaultSearch = `${base} taskStatus:${LIST_DEFAULT_TASK_STATUSES}`;
  const defaultCalendarSearch = `${base} taskStatus:${CALENDAR_DEFAULT_TASK_STATUSES}`;

  return (
    <>
      <Typography variant="h5" mb={1}>
        Facility Tasks
      </Typography>
      <Typography variant="body2" color="text.primary" mb={3}>
        Showing all current facility tasks. To see all tasks, open{" "}
        <MuiLink component={Link} to={`/o/${tenantId}/tasks`} underline="none">
          Tasks
        </MuiLink>
        .
      </Typography>
      <Box sx={{ mx: -3 }}>
        <TasksDisplay
          additionalFilterColumns={[]}
          commandButtons={commandButtons}
          defaultSearch={defaultSearch}
          defaultCalendarSearch={defaultCalendarSearch}
          persistenceKey={`facility-tasks-${facilityId}`}
          topContent={
            <Box sx={{ mx: 3 }}>
              <TaskFilterChips facilityId={facilityId} />
            </Box>
          }
        />
      </Box>
      {openAddModal && (
        <AddTaskDialog
          open={openAddModal}
          onClose={() => setOpenAddModal(false)}
          initialFacilityIds={facilityId ? [facilityId] : []}
        />
      )}
    </>
  );
};
